import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { PartnerListInterface } from '@feature/dashboard/partners/types/partner-list';
import { SenderListInterface } from '@feature/dashboard/senders/types/sender-list';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasPlatformAccessDirective } from '@sharedModule/directives/has-platform-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { CountryInfo } from '@sharedModule/models/country.model';
import { loadSourceCountry } from '@sharedModule/store/source-country/source-country.actions';
import { selectSourceCountry } from '@sharedModule/store/source-country/source-country.selector';
import { getCountryInfoFromCountryCode } from '@sharedModule/utils/array.utils';
import { removeEmptyStrings } from '@sharedModule/utils/form.utils';
import { Subject, Observable, takeUntil } from 'rxjs';
import { WalletService } from '../../services/wallet.service';

@Component({
  selector: 'app-add-wallet',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    HasPlatformAccessDirective,
  ],
  templateUrl: './add-wallet.component.html',
  styleUrl: './add-wallet.component.scss',
})
export class AddWalletComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.wallets;
  addWalletForm!: FormGroup;
  sourceCountryList!: CountryInfo[];
  isSubmitLoading!: boolean;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private walletService: WalletService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      senderList: SenderListInterface[];
      partnerList: Observable<PartnerListInterface[]>;
      isSuperAdmin: boolean;
    },
    private messageHandlerService: MessageHandlerService,
    public closeDialogRef: MatDialogRef<AddWalletComponent>
  ) {}

  ngOnInit(): void {
    this.addWalletForm = new FormGroup({
      [this.attrConstant.partner.key]: new FormControl(
        '',
        this.data.isSuperAdmin ? [Validators.required] : []
      ),
      [this.attrConstant.sender.key]: new FormControl(
        '',
        !this.data.isSuperAdmin ? [Validators.required] : []
      ),
      [this.attrConstant.country.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.currency.key]: new FormControl(
        { value: '', disabled: true },
        [Validators.required]
      ),
    });
    this.getCountryList();
  }

  onPartnerChange(event: {
    value: number;
    selectOptions: PartnerListInterface[] | null;
  }): void {
    this.store.dispatch(
      loadSourceCountry({
        payload: {
          partner_id: event.value,
        },
      })
    );
  }

  getCountryList(): void {
    this.store
      .select(selectSourceCountry)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) this.sourceCountryList = data;
      });
  }

  onSourceCountryChange(event: {
    value: string;
    selectOptions: CountryInfo[] | null;
  }): void {
    const selectedSourceCountry = getCountryInfoFromCountryCode(
      event.value,
      event.selectOptions
    );
    if (selectedSourceCountry)
      this.addWalletForm.patchValue({
        [this.attrConstant.currency.key]:
          selectedSourceCountry.currencySupported[0].currency,
      });
  }

  onCloseClick(): void {
    this.closeDialogRef.close();
  }

  onSubmit(): void {
    if (this.addWalletForm.valid) {
      this.isSubmitLoading = true;
      if (this.data.isSuperAdmin) this.addPartnerWallet();
      else this.addSenderWallet();
    }
  }

  addPartnerWallet(): void {
    this.walletService
      .addPartnerWallet(removeEmptyStrings(this.addWalletForm.getRawValue()))
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.closeDialogRef.close(true);
          }
        },
        error: err => {
          console.log(err);
          this.closeDialogRef.close(false);
        },
      });
  }

  addSenderWallet(): void {
    this.walletService
      .addSenderWallet(removeEmptyStrings(this.addWalletForm.getRawValue()))
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.closeDialogRef.close(true);
          }
        },
        error: err => {
          console.log(err);
          this.closeDialogRef.close(false);
        },
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
